import { useMemo } from "react";

import ReactDOMServer from "react-dom/server";

import { ChartLabelTooltip } from "app/components/React/ScatterChart/ChartLabelTooltip";
import { translate } from "app/filters/i18nFilter";

import { getMainDomain } from "../../../helpers/helpers";
import type { ScatterChartConfig } from "../types";
import type { FilterState } from "../utils/filtersReducer";
import { getAxisConfig, getVectorConfig, getXYConfig } from "../utils/scatterConfig";

import useSearchCompetitorsData from "./useSearchCompetitorsData";

interface ScatterDataParams {
    filters: FilterState;
}

const useCompetitorScatterData = ({ filters }: ScatterDataParams) => {
    const { scatterDataItems, mainSiteData, minMaxValues, isLoading } = useSearchCompetitorsData({ filters });
    const mainDomain = getMainDomain();
    const axisConfig = useMemo(() => getAxisConfig(), []);

    const { xAxis, yAxis } = axisConfig;

    const metrics = useMemo(
        () => ({
            x: getXYConfig({ axis: xAxis, mainSiteData }),
            y: getXYConfig({ axis: yAxis, mainSiteData }),
        }),
        [xAxis, yAxis, mainSiteData],
    );

    const config: ScatterChartConfig = useMemo(
        () => ({
            chart: {
                height: "auto",
                spacing: [0, 0, 0, 0],
            },
            xAxis: getVectorConfig({ axis: xAxis, minMaxValues }),
            yAxis: getVectorConfig({ axis: yAxis, minMaxValues }),
            tooltip: {
                split: true,
                outside: true,
                formatter: ({ chart }) =>
                    ReactDOMServer.renderToString(
                        <ChartLabelTooltip
                            tooltipWidth="400px"
                            chart={chart}
                            getAverageForBenchmark={() => mainSiteData}
                            xVertical={xAxis}
                            yVertical={yAxis}
                            aboveAvgText={`${translate("analysis.source.search.overview.search.competitors.above")} ${mainDomain}`}
                            belowAvgText={`${translate("analysis.source.search.overview.search.competitors.below")} ${mainDomain}`}
                        />,
                    ),
            },
        }),
        [yAxis, xAxis, minMaxValues, mainSiteData, mainDomain],
    );

    return {
        scatterDataItems,
        isLoading,
        metrics,
        config,
    };
};

export default useCompetitorScatterData;
